import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AddDialogParams,
  TimeSeries,
  TimeSeriesWithHistory,
  UpdateDialogParams,
} from '@clean-code/models';
import { DxPivotGridTypes } from 'devextreme-angular/ui/pivot-grid';
import { CellClickEvent } from 'devextreme/ui/pivot_grid';
import { Field } from 'devextreme/ui/pivot_grid/data_source';
import { tap } from 'rxjs';
import { AddCellDataDialogComponent } from '../components/add-cell-data-dialog/add-cell-data-dialog.component';
import { AddRowDialogComponent } from '../components/add-row-dialog/add-row-dialog.component';
import { CellDialogComponent } from '../components/cell-dialog/cell-dialog.component';
import { DuplicateCellDataDialogComponent } from '../components/duplicate-cell-data-dialog/duplicate-cell-data-dialog.component';
import {
  NullCell,
  PivotTableFunctionsService,
} from './functions/pivot-table-functions.service';
import { PivotTableService } from './pivot-table.service';

@Injectable({
  providedIn: 'root',
})
export class PivotTableEventsService implements OnDestroy {
  public dialog = inject(MatDialog);
  public pivotTableService = inject(PivotTableService);
  public pivotTableFunctions = inject(PivotTableFunctionsService);
  private dataSource: TimeSeries[] = [];
  private dataSourceHistory: TimeSeriesWithHistory[] = [];
  private dataSourceIntraday: TimeSeries[] = [];
  private duplicateData: { tradeDate: Date; identifier: string }[] = [];
  private columns: Array<Field> = [];
  private nullCells: NullCell[] = [];

  columns$ = this.pivotTableService.pivotTableObservable
    .pipe(tap((data) => (this.columns = data)))
    .subscribe();

  setSources(
    data: TimeSeries[],
    historyData: TimeSeriesWithHistory[],
    duplicateData: { tradeDate: Date; identifier: string }[],
  ) {
    this.dataSource = data;
    this.dataSourceIntraday = data.filter((f) => f.isIntraday);
    this.dataSourceHistory = historyData;
    this.duplicateData = duplicateData;
    this.nullCells = [];
    this.nullCells = this.pivotTableFunctions.setNullCells(data);
  }

  onCellPrepared(event: DxPivotGridTypes.CellPreparedEvent) {
    if (event.area !== 'data') return; // only data area. Other areas are column and row headers
    const cellValue = event.cell.value;
    if (cellValue === undefined) {
      // cell value can be undefined because the record does not exist in the database
      this.pivotTableFunctions.cellValues(event, null, null, '');
      return;
    }
    const position = this.pivotTableFunctions.cellPosition(event);

    const currentObject = this.pivotTableFunctions.findMatch(
      position,
      this.dataSource,
    ) as TimeSeries;

    const duplicateMatch = this.pivotTableFunctions.findMatch(
      position,
      this.duplicateData,
    );

    if (duplicateMatch) {
      this.pivotTableFunctions.cellStyle(event, '#f25c54', '#000000');
      this.pivotTableFunctions.cellValues(event, 'N/A', 'N/A', 'N/A');
      return;
    }

    const columnName = this.pivotTableFunctions.columnHelper(
      this.columns,
      event.cell.dataIndex,
    );

    const isNullFindMatch = this.nullCells.some(
      (record) =>
        record.tradeDate === position.rowDate &&
        record.identifier === position.columnSymbol &&
        record.nullFields.includes(columnName),
    );

    const isIntraday = this.pivotTableFunctions.findMatch(
      position,
      this.dataSourceIntraday,
    );

    const hasHistoryFind = this.dataSourceHistory.find((x) => {
      return x.timeSeriesId === currentObject?.id;
    });
    if (
      hasHistoryFind &&
      isIntraday &&
      columnName.toLowerCase() === 'settlementprice'
    ) {
      this.pivotTableFunctions.cellStyle(event, '#f4a261', '#000000');
    } else if (isIntraday && columnName.toLowerCase() === 'settlementprice') {
      this.pivotTableFunctions.cellStyle(event, '#06d6a0', '#000000');
    } else if (
      hasHistoryFind &&
      hasHistoryFind.fieldName.toLowerCase() === columnName.toLowerCase()
    ) {
      this.pivotTableFunctions.cellStyle(event, '#48cae4', '#000000');
    }

    if (isNullFindMatch) {
      this.pivotTableFunctions.cellValues(event, null, null, '');
    }
  }

  public onCellClick(event: CellClickEvent) {
    if (event.area !== 'data') return;
    const position = this.pivotTableFunctions.cellPosition(event);

    const currentObject = this.pivotTableFunctions.findMatch(
      position,
      this.dataSource,
    ) as TimeSeries;
    const columnName = this.pivotTableFunctions.columnHelper(
      this.columns,
      event.cell.dataIndex,
    );

    if (!currentObject) {
      const dialogParams: AddDialogParams = {
        position: {
          columnSymbol: position.columnSymbol,
          rowDate: new Date(position.rowDate),
          unit: position.unit,
        },
        columnName: columnName,
      };
      this.dialog.open(AddCellDataDialogComponent, {
        height: '300px',
        width: '300px',
        data: dialogParams,
        disableClose: true,
      });
      return;
    }
    this.pivotTableService.setIdAndColumnName(currentObject.id, columnName);
    const cellValue = event.cell.value;
    if (cellValue === 'N/A') {
      const dialogParams: any = {
        position: position,
      };

      this.dialog.open(DuplicateCellDataDialogComponent, {
        height: '400px',
        maxWidth: '1000px',
        data: dialogParams,
        autoFocus: false,
        disableClose: true,
      });
    } else {
      const dialogParams: UpdateDialogParams = {
        data: currentObject,
        columnName: columnName,
        cellValue: cellValue,
      };

      this.dialog.open(CellDialogComponent, {
        height: '500px',
        width: '600px',
        data: dialogParams,
        disableClose: true,
      });
    }
  }

  public onAddClick(event: CellClickEvent): void {
    this.dialog.open(AddRowDialogComponent, {
      height: '420px',
      width: '800px',
      autoFocus: false,
      disableClose: true,
    });
  }

  ngOnDestroy(): void {
    this.columns$.unsubscribe();
  }
}
